import apiClient from './client';
export interface Assessment {
    id: number;
    name: string;
    date: string;
    location?: string | null;
    status?: string | null;
    isDoubleAssessment?: boolean;
}
export interface AssessmentParticipant {
    id: number;
    participantType: string;
    status: string;
    teamKey?: 'A' | 'B';
    portalCandidate?: {
        id: number;
        name: string;
        portalStatus?: string;
        spouseName?: string | null;
    };
}
export interface AssessmentDetail extends Assessment {
    participants?: AssessmentParticipant[];
    interviewAreas?: InterviewArea[];
}
export interface ScheduleTemplateSlotInput {
    dayIndex?: number;
    startTime: string;
    endTime: string;
    label?: string | null;
    slotType?: 'session' | 'break' | 'meal' | 'other';
    // If true, this single entry is an interview period that expands into one slot per interview area.
    isInterviewPeriod?: boolean;
    interviewPeriodIndex?: number | null;
    track?: string | null;
    interviewer?: string | null;
    location?: string | null;
    visibility?: 'all' | 'assigned';
    sortOrder?: number;
    metadata?: any;
}
export interface ScheduleTemplate {
    id: number;
    name: string;
    description?: string | null;
    isDefault: boolean;
    slots: Array<{
        id: number;
        dayIndex: number;
        startTime: string;
        endTime: string;
        label?: string | null;
        slotType: 'session' | 'break' | 'meal' | 'other';
        isInterviewPeriod?: boolean;
        interviewPeriodIndex?: number | null;
        track?: string | null;
        interviewer?: string | null;
        location?: string | null;
        visibility: 'all' | 'assigned';
        sortOrder: number;
        metadata?: any;
    }>;
}
export interface ScheduleSlot {
    id: number;
    dayIndex: number;
    startTime: string;
    endTime: string;
    label?: string | null;
    slotType: 'session' | 'break' | 'meal' | 'other';
    interviewPeriodIndex?: number | null;
    interviewAreaId?: number | null;
    teamKey?: 'A' | 'B' | null;
    track?: string | null;
    interviewer?: string | null;
    location?: string | null;
    visibility: 'all' | 'assigned';
    assignments?: Array<{
        id: number;
        participantId: number;
        participant?: AssessmentParticipant;
    }>;
}
export interface MasterScheduleItem {
    id: number;
    dayIndex: number;
    startTime: string;
    endTime: string;
    startTimeLabel?: string;
    endTimeLabel?: string;
    label: string;
    slotType: 'session' | 'break' | 'meal' | 'other' | 'interview';
    interviewPeriodIndex?: number | null;
    location?: string | null;
    visibility: 'all' | 'assigned';
    sortOrder?: number;
    representativeSlotId: number;
}
export interface MasterSchedule {
    assessmentId: number;
    assessmentDate: string;
    days: Array<{
        dayIndex: number;
        date: string | null;
        items: MasterScheduleItem[];
    }>;
}
export interface InterviewArea {
    id: number;
    assessmentId: number;
    name: string;
    sortOrder: number;
    assessorUserId?: number | null;
    assessorName?: string | null;
    assessorUser?: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
    } | null;
}
export interface AssessmentRoleDefinition {
    id: number;
    name: string;
    displayName: string;
    description?: string | null;
    category?: string;
    roleType: 'assessor' | 'support';
    isInterviewArea: boolean;
    interviewAudience: 'couple' | 'candidate_only' | 'spouse_only';
    sortOrder: number;
    isActive: boolean;
}
export interface AssessmentInterviewQuestion {
    id: number;
    guideId: number;
    section?: string | null;
    prompt: string;
    helpText?: string | null;
    sortOrder: number;
    isActive: boolean;
}
export interface AssessmentInterviewGuide {
    id: number;
    title: string;
    subtitle?: string | null;
    intro?: string | null;
    roleId?: number | null;
    seedKey?: string | null;
    isActive: boolean;
    role?: Pick<AssessmentRoleDefinition, 'id' | 'name' | 'displayName' | 'isInterviewArea'> | null;
    questionCount?: number;
    questions?: AssessmentInterviewQuestion[];
}
export interface AssessmentEventRole {
    id: number;
    assessmentId: number;
    roleId: number;
    assignedUserId?: number | null;
    assignedName?: string | null;
    assistantUserId?: number | null;
    assistantName?: string | null;
    role?: AssessmentRoleDefinition;
    assignedUser?: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
    } | null;
    assistantUser?: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
    } | null;
    teamAssignments?: Array<{
        id: number;
        teamKey: 'A' | 'B';
        assignedUserId?: number | null;
        assignedName?: string | null;
        assistantUserId?: number | null;
        assistantName?: string | null;
        assignedUser?: {
            id: number;
            firstName: string;
            lastName: string;
            email: string;
        } | null;
        assistantUser?: {
            id: number;
            firstName: string;
            lastName: string;
            email: string;
        } | null;
    }>;
}
export interface AssessmentInterviewMatrixCell {
    id: number;
    assessmentId: number;
    interviewPeriodIndex: number;
    roleId: number;
    participantId?: number | null;
    participantPerson?: 'couple' | 'candidate' | 'spouse' | null;
    teamKey?: 'A' | 'B';
}
export interface AssessmentReport {
    id: number;
    assignmentId: number;
    category: string;
    title: string;
    content: string;
    initialOverallRating?: 'red' | 'yellow' | 'green' | null;
    initialAreaRating?: 'red' | 'yellow' | 'green' | null;
    afterPartNotes?: string | null;
    status: 'draft' | 'submitted' | 'reviewed' | 'approved' | 'returned';
    updatedAt?: string;
    createdAt?: string;
}
export interface ReportAssignment {
    id: number | null;
    assessmentId: number;
    participantId: number;
    roleId: number;
    assessorId: number | null;
    status: string;
    teamKey?: 'A' | 'B';
    participant?: AssessmentParticipant;
    role?: {
        id: number;
        name: string;
        displayName: string;
        roleType: 'assessor' | 'support';
        isInterviewArea: boolean;
        interviewAudience?: 'couple' | 'candidate_only' | 'spouse_only';
        sortOrder?: number;
    };
    assessor?: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
    };
    latestReport?: AssessmentReport | null;
}
export const assessmentsService = {
    list: async (): Promise<{
        success: boolean;
        data: Assessment[];
    }> => {
        const res = await apiClient.get('/assessments');
        return res.data;
    },
    create: async (payload: Partial<Assessment> & {
        name: string;
        date: string;
    }) => {
        const res = await apiClient.post('/assessments', payload);
        return res.data as {
            success: boolean;
            data: Assessment;
        };
    },
    get: async (id: number): Promise<{
        success: boolean;
        data: AssessmentDetail;
    }> => {
        const res = await apiClient.get(`/assessments/${id}`);
        return res.data;
    },
    addPortalCandidate: async (assessmentId: number, portalCandidateId: number, teamKey: 'A' | 'B' = 'A') => {
        const res = await apiClient.post(`/assessments/${assessmentId}/participants/portal-candidates`, { portalCandidateId, teamKey });
        return res.data;
    },
    listReportAssignments: async (assessmentId: number): Promise<{
        success: boolean;
        data: ReportAssignment[];
    }> => {
        const res = await apiClient.get(`/assessments/${assessmentId}/report-assignments`);
        return res.data;
    },
    deleteAssessmentReport: async (assessmentId: number, assignmentId: number): Promise<{
        success: boolean;
        deletedCount: number;
    }> => {
        const res = await apiClient.delete(`/assessments/${assessmentId}/report-assignments/${assignmentId}/report`);
        return res.data;
    },
    sendAttendanceReminder: async (assessmentId: number): Promise<{
        success: boolean;
        notified?: number;
        emailed?: number;
        message?: string;
    }> => {
        const res = await apiClient.post(`/assessments/${assessmentId}/attendance/reminders`);
        return res.data;
    },
    removeParticipant: async (assessmentId: number, participantId: number) => {
        const res = await apiClient.delete(`/assessments/${assessmentId}/participants/${participantId}`);
        return res.data;
    },
    // Templates
    listTemplates: async (): Promise<{
        success: boolean;
        data: ScheduleTemplate[];
    }> => {
        const res = await apiClient.get('/assessments/schedule-templates');
        return res.data;
    },
    createTemplate: async (payload: {
        name: string;
        description?: string;
        isDefault?: boolean;
        slots?: ScheduleTemplateSlotInput[];
    }) => {
        const res = await apiClient.post('/assessments/schedule-templates', payload);
        return res.data as {
            success: boolean;
            data: ScheduleTemplate;
        };
    },
    updateTemplate: async (templateId: number, payload: {
        name?: string;
        description?: string;
        isDefault?: boolean;
        slots?: ScheduleTemplateSlotInput[];
    }) => {
        const res = await apiClient.put(`/assessments/schedule-templates/${templateId}`, payload);
        return res.data as {
            success: boolean;
            data: ScheduleTemplate;
        };
    },
    applyTemplate: async (assessmentId: number, templateId: number, replace: boolean = true) => {
        const res = await apiClient.post(`/assessments/${assessmentId}/schedule/apply-template`, { templateId, replace });
        return res.data;
    },
    // Schedule
    getSchedule: async (assessmentId: number): Promise<{
        success: boolean;
        data: ScheduleSlot[];
    }> => {
        const res = await apiClient.get(`/assessments/${assessmentId}/schedule`);
        return res.data;
    },
    getMasterSchedule: async (assessmentId: number): Promise<{
        success: boolean;
        data: MasterSchedule;
    }> => {
        const res = await apiClient.get(`/assessments/${assessmentId}/master-schedule`);
        return res.data;
    },
    createSlot: async (assessmentId: number, payload: {
        dayIndex?: number;
        startTime: string;
        endTime: string;
        label?: string | null;
        slotType?: 'session' | 'break' | 'meal' | 'other';
        location?: string | null;
        visibility?: 'all' | 'assigned';
        sortOrder?: number;
        isInterviewPeriod?: boolean;
        interviewPeriodIndex?: number | null;
    }): Promise<{
        success: boolean;
        data: MasterSchedule;
    }> => {
        const res = await apiClient.post(`/assessments/${assessmentId}/schedule/slots`, payload);
        return res.data;
    },
    updateSlot: async (assessmentId: number, slotId: number, payload: {
        dayIndex?: number;
        startTime?: string;
        endTime?: string;
        label?: string | null;
        slotType?: 'session' | 'break' | 'meal' | 'other';
        location?: string | null;
        visibility?: 'all' | 'assigned';
        sortOrder?: number;
    }): Promise<{
        success: boolean;
        data: MasterSchedule;
    }> => {
        const res = await apiClient.put(`/assessments/${assessmentId}/schedule/slots/${slotId}`, payload);
        return res.data;
    },
    deleteSlot: async (assessmentId: number, slotId: number): Promise<{
        success: boolean;
        data: MasterSchedule;
    }> => {
        const res = await apiClient.delete(`/assessments/${assessmentId}/schedule/slots/${slotId}`);
        return res.data;
    },
    assignToSlot: async (assessmentId: number, slotId: number, participantId: number) => {
        const res = await apiClient.post(`/assessments/${assessmentId}/schedule/slots/${slotId}/assign`, { participantId });
        return res.data;
    },
    unassignFromSlot: async (assessmentId: number, slotId: number, participantId: number) => {
        const res = await apiClient.delete(`/assessments/${assessmentId}/schedule/slots/${slotId}/assign/${participantId}`);
        return res.data;
    },
    autoPopulateSchedule: async (assessmentId: number, replace: boolean = true, teamKey: 'A' | 'B' = 'A') => {
        const res = await apiClient.post(`/assessments/${assessmentId}/schedule/auto-populate`, { replace, teamKey });
        return res.data as {
            success: boolean;
            data?: any;
            message?: string;
        };
    },
    // Interview matrix (admin only; logical distribution, no times/days)
    getInterviewMatrix: async (assessmentId: number) => {
        const res = await apiClient.get(`/assessments/${assessmentId}/interview-matrix`);
        return res.data as {
            success: boolean;
            data: {
                roles: AssessmentRoleDefinition[];
                participants: AssessmentParticipant[];
                cells: AssessmentInterviewMatrixCell[];
                periods: number[];
            };
        };
    },
    setInterviewMatrixCell: async (assessmentId: number, payload: {
        interviewPeriodIndex: number;
        roleId: number;
        participantId: number | null;
        teamKey?: 'A' | 'B';
    }) => {
        const res = await apiClient.post(`/assessments/${assessmentId}/interview-matrix/cell`, payload);
        return res.data;
    },
    autoPopulateInterviewMatrix: async (assessmentId: number, replace: boolean = true, teamKey: 'A' | 'B' = 'A') => {
        const res = await apiClient.post(`/assessments/${assessmentId}/interview-matrix/auto-populate`, { replace, teamKey });
        return res.data as {
            success: boolean;
            data?: any;
            message?: string;
        };
    },
    // Interview areas (admin only)
    listInterviewAreas: async (assessmentId: number): Promise<{
        success: boolean;
        data: InterviewArea[];
    }> => {
        const res = await apiClient.get(`/assessments/${assessmentId}/interview-areas`);
        return res.data;
    },
    createInterviewArea: async (assessmentId: number, payload: Pick<InterviewArea, 'name' | 'sortOrder' | 'assessorUserId' | 'assessorName'>): Promise<{
        success: boolean;
        data: InterviewArea;
    }> => {
        const res = await apiClient.post(`/assessments/${assessmentId}/interview-areas`, payload);
        return res.data;
    },
    updateInterviewArea: async (assessmentId: number, areaId: number, payload: Partial<Pick<InterviewArea, 'name' | 'sortOrder' | 'assessorUserId' | 'assessorName'>>): Promise<{
        success: boolean;
        data: InterviewArea;
    }> => {
        const res = await apiClient.put(`/assessments/${assessmentId}/interview-areas/${areaId}`, payload);
        return res.data;
    },
    deleteInterviewArea: async (assessmentId: number, areaId: number) => {
        const res = await apiClient.delete(`/assessments/${assessmentId}/interview-areas/${areaId}`);
        return res.data;
    },
    // Global role catalog (admin)
    listRoleDefinitions: async (): Promise<{
        success: boolean;
        data: AssessmentRoleDefinition[];
    }> => {
        const res = await apiClient.get('/assessments/roles');
        return res.data;
    },
    createRoleDefinition: async (payload: Pick<AssessmentRoleDefinition, 'name' | 'displayName' | 'roleType' | 'isInterviewArea' | 'interviewAudience' | 'sortOrder'> & {
        description?: string | null;
        category?: string;
    }): Promise<{
        success: boolean;
        data: AssessmentRoleDefinition;
    }> => {
        const res = await apiClient.post('/assessments/roles', payload);
        return res.data;
    },
    updateRoleDefinition: async (roleId: number, payload: Partial<Pick<AssessmentRoleDefinition, 'name' | 'displayName' | 'roleType' | 'isInterviewArea' | 'interviewAudience' | 'sortOrder' | 'isActive'>> & {
        description?: string | null;
        category?: string;
    }): Promise<{
        success: boolean;
        data: AssessmentRoleDefinition;
    }> => {
        const res = await apiClient.put(`/assessments/roles/${roleId}`, payload);
        return res.data;
    },
    deleteRoleDefinition: async (roleId: number) => {
        const res = await apiClient.delete(`/assessments/roles/${roleId}`);
        return res.data;
    },
    listInterviewGuides: async (): Promise<{
        success: boolean;
        data: AssessmentInterviewGuide[];
    }> => {
        const res = await apiClient.get('/assessments/interview-guides');
        return res.data;
    },
    getInterviewGuide: async (guideId: number): Promise<{
        success: boolean;
        data: AssessmentInterviewGuide;
    }> => {
        const res = await apiClient.get(`/assessments/interview-guides/${guideId}`);
        return res.data;
    },
    createInterviewGuide: async (payload: {
        title: string;
        subtitle?: string | null;
        intro?: string | null;
        roleId?: number | null;
    }): Promise<{
        success: boolean;
        data: AssessmentInterviewGuide;
    }> => {
        const res = await apiClient.post('/assessments/interview-guides', payload);
        return res.data;
    },
    updateInterviewGuide: async (guideId: number, payload: Partial<Pick<AssessmentInterviewGuide, 'title' | 'subtitle' | 'intro' | 'roleId' | 'isActive'>>): Promise<{
        success: boolean;
        data: AssessmentInterviewGuide;
    }> => {
        const res = await apiClient.put(`/assessments/interview-guides/${guideId}`, payload);
        return res.data;
    },
    deleteInterviewGuide: async (guideId: number) => {
        const res = await apiClient.delete(`/assessments/interview-guides/${guideId}`);
        return res.data;
    },
    createInterviewQuestion: async (guideId: number, payload: {
        section?: string | null;
        prompt: string;
        helpText?: string | null;
        sortOrder?: number | null;
    }): Promise<{
        success: boolean;
        data: AssessmentInterviewQuestion;
    }> => {
        const res = await apiClient.post(`/assessments/interview-guides/${guideId}/questions`, payload);
        return res.data;
    },
    updateInterviewQuestion: async (guideId: number, questionId: number, payload: Partial<Pick<AssessmentInterviewQuestion, 'section' | 'prompt' | 'helpText' | 'sortOrder' | 'isActive'>>): Promise<{
        success: boolean;
        data: AssessmentInterviewQuestion;
    }> => {
        const res = await apiClient.put(`/assessments/interview-guides/${guideId}/questions/${questionId}`, payload);
        return res.data;
    },
    deleteInterviewQuestion: async (guideId: number, questionId: number) => {
        const res = await apiClient.delete(`/assessments/interview-guides/${guideId}/questions/${questionId}`);
        return res.data;
    },
    reorderInterviewQuestions: async (guideId: number, questions: Array<{
        id: number;
        sortOrder: number;
        section?: string | null;
    }>): Promise<{
        success: boolean;
        data: AssessmentInterviewQuestion[];
    }> => {
        const res = await apiClient.put(`/assessments/interview-guides/${guideId}/questions/reorder`, { questions });
        return res.data;
    },
    // Per assessment (admin)
    listEventRoles: async (assessmentId: number): Promise<{
        success: boolean;
        data: AssessmentEventRole[];
    }> => {
        const res = await apiClient.get(`/assessments/${assessmentId}/roles`);
        return res.data;
    },
    setEventRoles: async (assessmentId: number, roleIds: number[]): Promise<{
        success: boolean;
        data: AssessmentEventRole[];
    }> => {
        const res = await apiClient.put(`/assessments/${assessmentId}/roles`, { roleIds });
        return res.data;
    },
    assignEventRole: async (assessmentId: number, roleId: number, payload: {
        assignedUserId?: number | null;
        assignedName?: string | null;
        assistantUserId?: number | null;
        assistantName?: string | null;
    }): Promise<{
        success: boolean;
        data: AssessmentEventRole;
    }> => {
        const res = await apiClient.put(`/assessments/${assessmentId}/roles/${roleId}/assignment`, payload);
        return res.data;
    },
    assignEventRoleTeam: async (assessmentId: number, roleId: number, teamKey: 'A' | 'B', payload: {
        assignedUserId?: number | null;
        assignedName?: string | null;
        assistantUserId?: number | null;
        assistantName?: string | null;
    }): Promise<{
        success: boolean;
        data: AssessmentEventRole;
    }> => {
        const res = await apiClient.put(`/assessments/${assessmentId}/roles/${roleId}/team-assignments/${teamKey}`, payload);
        return res.data;
    },
    updateTeamMode: async (assessmentId: number, isDoubleAssessment: boolean) => {
        const res = await apiClient.patch(`/assessments/${assessmentId}/team-mode`, { isDoubleAssessment });
        return res.data as {
            success: boolean;
            data: Assessment;
        };
    },
    updateParticipantTeam: async (assessmentId: number, participantId: number, teamKey: 'A' | 'B') => {
        const res = await apiClient.patch(`/assessments/${assessmentId}/participants/${participantId}/team`, { teamKey });
        return res.data;
    },
};
